import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import clsx from 'clsx';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { $dashboardSpendingSummaryStore, $initialCurrenciesAvailableStore } from '@lib/store';
import { useCurrencySymbol } from '@shared/hooks';
import { AmountUtils, DateUtils, PaletteUtils } from '@shared/utils';
import { Select, Text } from '@shared/widgets';
import styles from './dashboardSpendingCurrency.module.scss';

const getNumberPostfix = (num: number): string => {
  const mod = num % 10;
  const thDates = [11, 12, 13];

  if (thDates.includes(num)) return 'th';

  switch (mod) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

const dates = DateUtils.getLastMonths();

export const DashboardSpendingCurrency = () => {
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const dashboardSpendingSummaryStore = useUnit($dashboardSpendingSummaryStore.store);

  const [currency, setCurrency] = useState<string>(currenciesAvailableStore.data[0]);
  const [date, setDate] = useState<string>(dates[0].value);

  useEffect(() => {
    if (currency) {
      $dashboardSpendingSummaryStore.events.update({ currency, date });
    }
  }, [currency, date]);

  const symbol = useCurrencySymbol(currency);

  const colors = useMemo(() => {
    if (PaletteUtils.isVcfcPalette) return PaletteUtils.spendingByCurrencyVcfcColors;
    return PaletteUtils.spendingByCurrencyInsigniaColors;
  }, []);

  const options = useMemo<ApexOptions>(() => {
    return {
      chart: {
        id: 'bar',
        width: '100%',
        redrawOnParentResize: true,
        redrawOnWindowResize: true,
        toolbar: {
          show: false,
        },
      },
      noData: {
        text: 'No data',
        style: {
          color: 'var(--text-slate)',
        },
      },
      colors: colors,
      stroke: {
        width: [1, 0, 0],
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'dark',
          type: 'vertical',
          gradientToColors: ['#d7c29a', '#fff'],
          inverseColors: false,
          shadeIntensity: 1,
          opacityFrom: 0.4,
          opacityTo: 0.1,
          stops: [0, 90, 100],
        },
        pattern: {
          style: 'verticalLines',
          width: 6,
          height: 6,
          strokeWidth: 2,
        },
      },
      xaxis: {
        tickAmount: 15,
        type: 'category',
        labels: {
          show: true,
          style: {
            colors: 'var(--text-slate)',
          },
        },
        formatter: function (value: any) {
          return value;
        },
        categories: dashboardSpendingSummaryStore.data.map((s) => DateUtils.formatDate(s.date)),
      },
      yaxis: {
        tickAmount: 4,
        labels: {
          show: true,
          style: {
            colors: ['var(--text-slate)'],
            fontSize: '14px',
            fontFamily: '"Proxima Nova", sans-serif',
            fontWeight: 500,
          },
          offsetX: -10,
          offsetY: 0,
          formatter: function (value: number) {
            if (value < 1000) return value.toString();
            return value / 1000 + 'k';
          },
        },
        axisTicks: {
          show: true,
          color: '#ededed',
          width: 16,
          offsetX: 13,
          offsetY: 1,
        },
        tooltip: {
          enabled: false,
          offsetX: 0,
        },
      },
      markers: {
        size: 5,
        strokeWidth: 1,
        fillOpacity: 0,
        strokeOpacity: 0,
        hover: {
          size: 8,
        },
      },
      tooltip: {
        enabled: true,
        theme: 'dark',
        x: {
          show: true,
          format: 'dd MMM yyyy',
          formatter: function (value) {
            const date = new Date(dashboardSpendingSummaryStore.data[value - 1].date).getDate();
            return `Day: ${date}${getNumberPostfix(date)}`;
          },
        },
        y: {
          formatter: function (value) {
            if (symbol) {
              return symbol + AmountUtils.transformToAmountWithCurrency(value);
            } else {
              return '';
            }
          },
        },
      },
      grid: {
        show: true,
        borderColor: '#ededed',
        strokeDashArray: 10,
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
    };
  }, [dashboardSpendingSummaryStore, currency, date]);
  const series = useMemo(
    () => [{ name: currency, type: 'area', data: dashboardSpendingSummaryStore.data.map((s) => s.amount) }],
    [dashboardSpendingSummaryStore, currency],
  );

  return (
    <div className='flex flex-col gap-6'>
      <div className='flex justify-between'>
        <Text weight='medium' size='2xl'>
          Spending by currency
        </Text>
        <div className='flex gap-4'>
          <Select className='min-w-[100px]' items={currenciesAvailableStore.data} value={currency} onChange={(value) => setCurrency(value!)} />
          <Select
            className='min-w-40'
            items={dates.map((d) => ({ value: d.value, label: `${d.name} ${new Date(d.value).getFullYear()}` }))}
            value={date}
            onChange={(val) => setDate(val!)}
          />
        </div>
      </div>
      <div>
        <div className={clsx('-mb-[66px] py-4 px-[124px] bg-ice dark:bg-eerie-black relative z-10', styles['apexcharts-title-label'])}>
          <span className='before:text-primary after:border-b-primary'>{currency?.toUpperCase()}</span>
        </div>
        <Chart options={options} series={series} type='line' height={500} />
      </div>
    </div>
  );
};
